import { createSlice, PayloadAction, createSelector } from "@reduxjs/toolkit";
import type { RootState } from "../store";
import { resetState, clearAuthStateOnly } from "./auth.slice";
import { debugLog, debugError, DEBUG_CATEGORIES } from "@/utils/debug";

export interface PerformanceState {
  isLowEndDevice: boolean;
  prefersReducedMotion: boolean;
  disableAnimations: boolean;
  performanceScore: number; // 0-100
  detectionComplete: boolean;
  manualOverride: "auto" | "optimized" | "full-performance" | null;
  metrics: {
    cores: number;
    memory: number;
    fps: number;
    connectionSpeed: string;
    devicePixelRatio: number;
  };
}

const STORAGE_KEY = "performance-settings";

// Carregar configurações do localStorage
const loadPerformanceSettings = (): Partial<PerformanceState> => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    debugError(DEBUG_CATEGORIES.PERF, "Erro ao carregar configurações de performance:", error);
  }
  return {};
};

// Salvar configurações no localStorage
const savePerformanceSettings = (state: PerformanceState) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    debugError(DEBUG_CATEGORIES.PERF, "Erro ao salvar configurações de performance:", error);
  }
};

const initialState: PerformanceState = {
  isLowEndDevice: false,
  prefersReducedMotion: false,
  disableAnimations: false,
  performanceScore: 100,
  detectionComplete: false,
  manualOverride: null,
  metrics: {
    cores: 4,
    memory: 4,
    fps: 60,
    connectionSpeed: "4g",
    devicePixelRatio: 1,
  },
  ...loadPerformanceSettings(),
};

export const performanceSlice = createSlice({
  name: "performance",
  initialState,
  reducers: {
    setPerformanceInfo(
      state: PerformanceState,
      { payload }: PayloadAction<{
        isLowEnd: boolean;
        prefersReducedMotion: boolean;
        performanceScore: number;
        metrics: PerformanceState["metrics"];
      }>
    ) {
      state.isLowEndDevice = payload.isLowEnd;
      state.prefersReducedMotion = payload.prefersReducedMotion;
      state.performanceScore = payload.performanceScore;
      state.metrics = payload.metrics;
      state.detectionComplete = true;

      // Aplicar override manual se existir
      if (state.manualOverride === "optimized") {
        state.disableAnimations = true;
      } else if (state.manualOverride === "full-performance") {
        state.disableAnimations = false;
      } else {
        // Auto: basear na detecção
        state.disableAnimations = payload.isLowEnd || payload.prefersReducedMotion;
      }

      debugLog(DEBUG_CATEGORIES.PERF, "Performance info atualizada:", {
        score: payload.performanceScore,
        isLowEnd: payload.isLowEnd,
        disableAnimations: state.disableAnimations,
      });

      savePerformanceSettings(state);
    },

    setManualOverride(
      state: PerformanceState,
      { payload }: PayloadAction<"auto" | "optimized" | "full-performance">
    ) {
      state.manualOverride = payload;

      // Aplicar override imediatamente
      if (payload === "optimized") {
        state.disableAnimations = true;
      } else if (payload === "full-performance") {
        state.disableAnimations = false;
      } else {
        // Auto: voltar para detecção automática
        state.disableAnimations = state.isLowEndDevice || state.prefersReducedMotion;
      }

      debugLog(DEBUG_CATEGORIES.PERF, "Override manual aplicado:", payload);

      savePerformanceSettings(state);
    },

    toggleAnimations(state: PerformanceState) {
      state.disableAnimations = !state.disableAnimations;
      state.manualOverride = state.disableAnimations ? "optimized" : "full-performance";

      debugLog(DEBUG_CATEGORIES.PERF, "Animações toggled:", state.disableAnimations);

      savePerformanceSettings(state);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(resetState, (state) => {
      debugLog(DEBUG_CATEGORIES.PERF, "Estado resetado, mantendo configurações de performance");
    });
    builder.addCase(clearAuthStateOnly, (state) => {
      debugLog(DEBUG_CATEGORIES.PERF, "Estado limpo por outra aba, mantendo configurações de performance");
    });
  },
});

export const performanceReducer = performanceSlice.reducer;
export const { setPerformanceInfo, setManualOverride, toggleAnimations } =
  performanceSlice.actions;

// Seletores
const performanceStateSelector = (state: RootState) => state.performanceReducer;

export const shouldDisableAnimationsSelector = createSelector(
  [performanceStateSelector],
  (state: PerformanceState) => state.disableAnimations
);

export const performanceScoreSelector = createSelector(
  [performanceStateSelector],
  (state: PerformanceState) => state.performanceScore
);

export const isLowEndDeviceSelector = createSelector(
  [performanceStateSelector],
  (state: PerformanceState) => state.isLowEndDevice
);

export const performanceMetricsSelector = createSelector(
  [performanceStateSelector],
  (state: PerformanceState) => state.metrics
);

export const detectionCompleteSelector = createSelector(
  [performanceStateSelector],
  (state: PerformanceState) => state.detectionComplete
);

export const manualOverrideSelector = createSelector(
  [performanceStateSelector],
  (state: PerformanceState) => state.manualOverride
);

export const performanceSelectors = {
  shouldDisableAnimations: shouldDisableAnimationsSelector,
  performanceScore: performanceScoreSelector,
  isLowEndDevice: isLowEndDeviceSelector,
  performanceMetrics: performanceMetricsSelector,
  detectionComplete: detectionCompleteSelector,
  manualOverride: manualOverrideSelector,
};
